/**
 * Utility functions for formatting values
 */

/**
 * Format file size in bytes to a human-readable string
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Sanitize a filename to prevent XSS and display issues
 * Removes or escapes potentially dangerous characters
 * @param {string} filename - The filename to sanitize
 * @param {number} maxLength - Maximum length (default: 100)
 * @returns {string} Sanitized filename
 */
export const sanitizeFilename = (filename, maxLength = 100) => {
  if (!filename || typeof filename !== 'string') return '';

  const escapeHtmlEntities = (value) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  // Remove any HTML tags (repeat until fully removed to prevent partial tag reappearance)
  let sanitized = filename;
  let previousSanitized;
  do {
    previousSanitized = sanitized;
    sanitized = sanitized.replace(/<[^>]*>/g, '');
  } while (sanitized !== previousSanitized);

  // Remove control characters (ASCII 0-31 and 127)
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');

  // Truncate if too long
  if (sanitized.length > maxLength) {
    const ELLIPSIS = '...';
    const lastDotIndex = sanitized.lastIndexOf('.');

    // Handle files without extension
    if (lastDotIndex === -1 || lastDotIndex === 0) {
      return sanitized.slice(0, maxLength - ELLIPSIS.length) + ELLIPSIS;
    }

    const ext = sanitized.slice(lastDotIndex + 1);
    const nameWithoutExt = sanitized.slice(0, lastDotIndex);
    // Calculate available space: maxLength - dot(1) - extension - ellipsis(3)
    const availableLength = maxLength - 1 - ext.length - ELLIPSIS.length;

    if (availableLength <= 0) {
      // Extension is too long, just truncate the whole thing
      return sanitized.slice(0, maxLength - ELLIPSIS.length) + ELLIPSIS;
    }

    const truncatedName = nameWithoutExt.slice(0, availableLength) + ELLIPSIS;
    sanitized = `${truncatedName}.${ext}`;
  }

  return escapeHtmlEntities(sanitized);
};

/**
 * Split a filename into base name and extension
 * @param {string} filename - The filename to split
 * @returns {{ baseName: string, extension: string }} The base name and extension (including dot)
 */
export const splitFileName = (filename) => {
  if (!filename || typeof filename !== 'string') return { baseName: '', extension: '' };
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) return { baseName: filename, extension: '' };
  return {
    baseName: filename.slice(0, lastDot),
    extension: filename.slice(lastDot),
  };
};
